import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { AlertTriangle, ArrowUpRight, Info, Pause, RefreshCw } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAuth } from '@/contexts/AuthContext';
import { canManageWorkspace } from '@/lib/permissions';
import type { AccountResponse, IdentityResponse } from '@/types/api';
import { useCheckNow } from '@/hooks/mutations/useCheckNow';
import { formatRelative } from '@/lib/format';
import { proxyImage } from '@/lib/imageProxy';
import { cn } from '@/lib/cn';

interface AccountRowProps {
  account: AccountResponse;
  identity: IdentityResponse | null;
}

export function AccountRow({ account, identity }: AccountRowProps) {
  const { t } = useI18n();
  const { user } = useAuth();
  const canManage = canManageWorkspace(user?.role);
  const check = useCheckNow();
  const hasErrors = account.consecutiveErrors > 0;
  const isPaused = Boolean(account.pausedReason);
  return (
    <article
      aria-label={t(`Compte @${account.username}`, `Account @${account.username}`)}
      className="group grid min-w-0 grid-cols-[minmax(0,1fr)_44px] items-center gap-x-3 gap-y-2 px-4 py-4 transition-colors hover:bg-bg-0/80 md:grid-cols-[minmax(0,1fr)_120px_130px_44px] md:py-3"
    >
      <Link
        to={`/accounts/${account.id}`}
        aria-label={`@${account.username}`}
        className="flex min-w-0 items-center gap-3 rounded-lg"
      >
        <Avatar className="size-10 shrink-0 border border-line-1">
          <AvatarImage src={proxyImage(account.profilePictureUrl)} alt="" />
          <AvatarFallback className="bg-violet-soft text-[12px] font-semibold text-violet-deep">
            {account.username.charAt(0).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <span className="flex min-w-0 flex-col gap-1">
          <span className="truncate text-[13px] font-semibold text-text-1 transition-colors group-hover:text-violet-deep">
            @{account.username}
          </span>
          <span className="flex min-w-0 items-center gap-1.5 text-[11px] text-text-3">
            {identity ? (
              <>
                <span
                  className="size-1.5 shrink-0 rounded-full"
                  style={{ background: identity.color ?? 'hsl(var(--text-3))' }}
                />
                <span className="truncate">{identity.name}</span>
              </>
            ) : (
              <span>{t('Sans identité', 'No identity')}</span>
            )}
          </span>
          <span className="text-[10px] text-text-3 md:hidden">
            {t('Dernière collecte', 'Last collection')} · {formatRelative(account.lastCheckedAt)}
          </span>
        </span>
      </Link>
      <div className="col-start-1 flex min-w-0 flex-wrap items-center gap-1.5 md:col-start-auto">
        {isPaused && (
          <AccountStatusBadge
            tone="amber"
            label={t('En pause', 'Paused')}
            icon={<Pause className="size-3" aria-hidden="true" />}
            title={t('Motif de la pause', 'Pause reason')}
            description={account.pausedReason}
          />
        )}
        {hasErrors && (
          <AccountStatusBadge
            tone="rose"
            label={t(
              `En erreur · ${account.consecutiveErrors}`,
              `With errors · ${account.consecutiveErrors}`,
            )}
            icon={<AlertTriangle className="size-3" aria-hidden="true" />}
            title={t('Dernière erreur', 'Latest error')}
            description={
              account.lastError ||
              t(
                `${account.consecutiveErrors} erreurs successives`,
                `${account.consecutiveErrors} consecutive errors`,
              )
            }
          />
        )}
        {!isPaused && !hasErrors && (
          <AccountStatusBadge
            tone={account.isActive ? 'emerald' : 'neutral'}
            label={account.isActive ? t('Actif', 'Active') : t('Inactif', 'Inactive')}
            icon={
              <span
                className={cn(
                  'size-1.5 shrink-0 rounded-full',
                  account.isActive ? 'bg-emerald' : 'bg-line-3',
                )}
                aria-hidden="true"
              />
            }
          />
        )}
      </div>
      <time
        dateTime={account.lastCheckedAt ?? undefined}
        className="hidden text-[11px] text-text-3 md:block"
      >
        <span className="sr-only">{t('Dernière collecte : ', 'Last collection: ')}</span>
        {formatRelative(account.lastCheckedAt)}
      </time>
      <div className="col-start-2 row-start-1 flex items-center justify-end md:col-start-auto md:row-start-auto">
        {canManage ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="size-11 rounded-lg text-text-3 hover:text-violet-deep sm:size-11"
                onClick={() => check.mutate(account.id)}
                disabled={check.isPending}
                aria-label={t('Scraper maintenant', 'Scrape now')}
              >
                <RefreshCw
                  className={cn('size-4', check.isPending && 'motion-safe:animate-spin')}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('Scraper maintenant', 'Scrape now')}</TooltipContent>
          </Tooltip>
        ) : (
          <Link
            to={`/accounts/${account.id}`}
            aria-label={t(`Ouvrir @${account.username}`, `Open @${account.username}`)}
            className="flex size-11 items-center justify-center rounded-lg text-text-3 hover:bg-bg-2 hover:text-violet-deep"
          >
            <ArrowUpRight className="size-4" />
          </Link>
        )}
      </div>
      {(hasErrors || isPaused) && (
        <p className="col-span-2 break-words text-[11px] leading-relaxed text-text-3 md:sr-only">
          {account.pausedReason}
          {account.pausedReason && account.lastError && ' · '}
          {account.lastError ||
            (hasErrors
              ? t(
                  `${account.consecutiveErrors} erreurs successives`,
                  `${account.consecutiveErrors} consecutive errors`,
                )
              : '')}
        </p>
      )}
    </article>
  );
}

function AccountStatusBadge({
  label,
  icon,
  tone,
  description,
  title,
}: {
  label: string;
  icon: ReactNode;
  tone: 'amber' | 'rose' | 'emerald' | 'neutral';
  description?: string | null;
  title?: string;
}) {
  const className = cn(
    'inline-flex min-h-7 max-w-full items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-medium leading-tight',
    tone === 'amber'
      ? 'border-amber/20 bg-amber-soft text-amber'
      : tone === 'rose'
        ? 'border-rose/20 bg-rose-soft text-rose'
        : tone === 'emerald'
          ? 'border-emerald/20 bg-emerald-soft text-emerald'
          : 'border-line-1 bg-bg-2 text-text-3',
  );
  if (!description)
    return (
      <span className={className}>
        {icon}
        <span className="min-w-0">{label}</span>
      </span>
    );
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={className}>
          {icon}
          <span className="min-w-0">{label}</span>
          <Info className="size-2.5 shrink-0" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        aria-label={title}
        align="start"
        className="max-h-[70dvh] w-72 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-xl p-3 text-[12px] leading-relaxed motion-reduce:animate-none"
      >
        <p className="mb-1.5 font-semibold text-text-1">{title}</p>
        <p className="break-words text-text-2">{description}</p>
      </PopoverContent>
    </Popover>
  );
}
